/**
 * Saved Rituals Service
 * API service for saved rituals
 */

import type {
  PaginationParams,
  SavedRitual,
  ToggleSaveResponse,
} from "@/features/profile/types/saved.types"
import { apiClient } from "@/lib/api-client"

const API_BASE = "/api/rituals/saved"

/**
 * Get Saved Rituals
 * Fetches paginated list of saved rituals for the current user
 */
export const getSavedRituals = async (
  params?: PaginationParams & { search?: string },
): Promise<{
  rituals: SavedRitual[]
  total: number
  page: number
  totalPages: number
  hasMore: boolean
}> => {
  const queryParams = new URLSearchParams()

  if (params?.page) queryParams.append("page", params.page.toString())
  if (params?.limit) queryParams.append("limit", params.limit.toString())
  if (params?.search) queryParams.append("search", params.search)

  const queryString = queryParams.toString()

  const { data } = await apiClient.get<{
    success: boolean
    data: {
      rituals: SavedRitual[]
      total: number
      page: number
      totalPages: number
      hasMore: boolean
    }
    message: string
  }>(`${API_BASE}${queryString ? `?${queryString}` : ""}`)

  return {
    rituals: data.data.rituals.map(item => ({
      ...item,
      isSaved: true, // Always true since user can only see their saved items
    })),
    total: data.data.total,
    page: data.data.page,
    totalPages: data.data.totalPages,
    hasMore: data.data.hasMore,
  }
}

/**
 * Toggle Save Ritual
 * Toggles a ritual as saved/unsaved
 */
export const toggleSavedRitual = async (ritualId: string): Promise<ToggleSaveResponse> => {
  const { data } = await apiClient.post<{
    success: boolean
    data: ToggleSaveResponse
    message: string
  }>(`${API_BASE}`, {
    ritualId,
  })

  return {
    success: data.data.success,
    message: data.data.message,
    saved: data.data.saved !== false, // Check if the item was saved (true) or unsaved (false)
  }
}
